import { useState, useEffect } from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  Navigate,
} from "react-router-dom";
import routes from "./constants/routes";
import roleRoutes from "./constants/roleRoutes";
import AppLayout from "./components/layouts/AppLayout";
import Home from "./pages/Home";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import Profile from "./pages/Profile";
import Academic from "./pages/Academic";
import Accounting from "./pages/Accounting";
import Admin from "./pages/Admin";
import Asset from "./pages/Asset";
import Course from "./components/academic/Course";
import { Amplify, Auth } from "aws-amplify";
import config from "./config";
import UserContext from "./contexts/user";
import { SnackbarProvider } from "notistack";
import Program from "./components/academic/Program";
import ProgramFeeDetails from "./components/accounting/ProgramFeeDetails";
import Account from "./components/accounting/Account";
import FeePayments from "./components/accounting/FeePayments";
import Calendar from "./pages/Calendar";
import { Quiz } from "./pages/Quiz";
import { SocketContextWrapper } from "./contexts/socket";
import { withRoot } from "./withRoot";
import theme from "./config/theme";
import NewSchedule from "./pages/NewSchedule";
import { DailyScheduleMain } from "./components/common/DailySchedule";
import AccountingManage from "./pages/AccountingManage";
import StudentView from "./pages/StudentView";
import FacultyView from "./pages/FacultyView";
import StaffView from "./pages/StaffView";
import LoadingContent from "./components/common/LoadingContent";

Amplify.configure({
  Auth: {
    mandatorySignIn: true,
    region: config.cognito.REGION,
    userPoolId: config.cognito.USER_POOL_ID,
    userPoolWebClientId: config.cognito.APP_CLIENT_ID,
  },
});

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(";").shift();
  }
}

const App = () => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const verifyUser = async () => {
      try {
        const currentUser = await Auth.currentAuthenticatedUser();
        setLoading(false);
        if (currentUser) {
          setUser(currentUser?.attributes);
          setUserRole(
            currentUser?.attributes && currentUser?.attributes["custom:type"]
          );
        }

        const storedToken = getCookie("authToken");
        if (storedToken) {
          const requestOptions = {
            method: "GET",
            headers: {
              Authorization: `Bearer ${storedToken}`,
              "Content-Type": "application/json",
            },
          };
          fetch("https://api.example.com/data", requestOptions)
            .then((response) => response.json())
            .then((data) => {
              // Handle the API response data
            })
            .catch((error) => {
              // Handle errors
            });
        }
      } catch (err) {
        setLoading(false);
        console.error(err);
        if (window.location.pathname !== "/") {
          window.location.href = "/";
        }
      }
    };
    verifyUser().catch(console.error);
  }, []);
  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider>
        <UserContext.Provider value={{ user, setUser, userRole, setUserRole }}>
          <SocketContextWrapper>
            <LoadingContent loading={loading}>
              <Router>
                <CssBaseline />
                {!user ? (
                  <Routes>
                    <Route
                      path={routes.RESET_PASSWORD}
                      element={<ResetPassword />}
                    />
                    <Route path="/" element={<Login />} />
                    {/* <Route
                    path="*"
                    element={<Navigate to={routes.HOME} replace />}
                  /> */}
                  </Routes>
                ) : (
                  <Routes>
                    <Route
                      element={
                        <AppLayout>
                          <Outlet />
                        </AppLayout>
                      }
                    >
                      <Route
                        path={routes.HOME}
                        element={<Navigate to={roleRoutes[userRole]} />}
                      />
                      <Route path={routes.CLASSES.HOME} element={<Home />} />
                      <Route
                        path={routes.CLASSES.UPCOMING}
                        element={<Home tab={0} />}
                      />
                      <Route
                        path={routes.CLASSES.PREVIOUS}
                        element={<Home tab={1} />}
                      />
                      <Route
                        path={routes.CLASSES.SUBMISSIONS}
                        element={<Home tab={2} />}
                      />
                      <Route
                        path={routes.CLASSES.ASSIGNMENTS}
                        element={<Home tab={2} submissionsTab={0} />}
                      />
                      <Route
                        path={routes.CLASSES.ASSIGNMENTQUESTIONS}
                        element={<Quiz />}
                      />
                      <Route
                        path={routes.CLASSES.EXAMS}
                        element={<Home tab={2} submissionsTab={1} />}
                      />
                      <Route
                        path={routes.CLASSES.EXAMQUESTIONS}
                        element={<Quiz />}
                      />
                      <Route
                        path={routes.ACADEMIC.HOME}
                        element={<Academic />}
                      />
                      <Route
                        path={routes.ACADEMIC.PROGRAMS}
                        element={<Academic tab={0} />}
                      />
                      <Route
                        path={routes.ACADEMIC.COURSES}
                        element={<Academic tab={1} />}
                      />
                      <Route
                        path={routes.ACADEMIC.COURSE}
                        element={<Course />}
                      />
                      <Route
                        path={routes.ACADEMIC.PROGRAM}
                        element={<Program />}
                      />
                      <Route
                        path={routes.ACADEMIC.NEW_SCHEDULE}
                        element={<NewSchedule />}
                      />
                      <Route path={routes.ASSETS.HOME} element={<Asset />} />
                      <Route
                        path={routes.ASSETS.ASSETS}
                        element={<Asset tab={0} />}
                      />
                      <Route
                        path={routes.ASSETS.INVENTORIES}
                        element={<Asset tab={1} />}
                      />
                      <Route path={routes.CALENDAR} element={<Calendar />} />
                      <Route
                        path={routes.NEW_SCHEDULE}
                        element={<NewSchedule />}
                      />
                      <Route
                        path={routes.ACCOUNTING.HOME}
                        element={<Accounting />}
                      />
                      <Route
                        path={routes.ACCOUNTING.MYEXPENSES}
                        element={<Accounting tab={0} />}
                      />
                      <Route
                        path={routes.ACCOUNTING.CASHREGISTER}
                        element={<Accounting tab={1} />}
                      />
                      <Route
                        path={routes.ACCOUNTING.ACCOUNTS}
                        element={<Accounting tab={2} />}
                      />
                      <Route
                        path={routes.ACCOUNTING.FEES}
                        element={<Accounting tab={3} />}
                      />
                      <Route
                        path={routes.ACCOUNTING.PROGRAMFEE}
                        element={<ProgramFeeDetails />}
                      />
                      <Route
                        path={routes.ACCOUNTING.REGISTERAPPROVALS}
                        element={<AccountingManage />}
                      />
                      <Route
                        path={routes.ACCOUNTING.FEEAPPROVALS}
                        element={<FeePayments />}
                      />
                      <Route
                        path={routes.ACCOUNTING.ACCOUNTTRANSFERS}
                        element={<Account />}
                      />
                      <Route path={routes.ADMIN.HOME} element={<Admin />} />
                      <Route
                        path={routes.ADMIN.SESSION}
                        element={<Admin tab={0} />}
                      />
                      <Route
                        path={routes.ADMIN.ADMISSION}
                        element={<Admin tab={1} />}
                      />
                      <Route
                        path={routes.ADMIN.INSTITUTE}
                        element={<Admin tab={2} />}
                      />
                      <Route
                        path={routes.ADMIN.STUDENTS}
                        element={<Admin tab={3} />}
                      />
                      <Route
                        path={routes.ADMIN.FACULTIES}
                        element={<Admin tab={4} />}
                      />
                      <Route
                        path={routes.ADMIN.STAFFS}
                        element={<Admin tab={5} />}
                      />
                      <Route
                        path={routes.ADMIN.STUDENT}
                        element={<StudentView />}
                      />
                      <Route
                        path={routes.ADMIN.FACULTY}
                        element={<FacultyView />}
                      />
                      <Route
                        path={routes.ADMIN.STAFF}
                        element={<StaffView />}
                      />

                      <Route
                        path={routes.DAILY_SCHEDULE}
                        element={<DailyScheduleMain />}
                      />
                      <Route
                        path={routes.ACTIVITY}
                        element={<DailyScheduleMain />}
                      />
                      <Route path={routes.PROFILE} element={<Profile />} />
                      <Route
                        path="*"
                        element={<Navigate to={routes.PROFILE} replace />}
                      />
                    </Route>
                  </Routes>
                )}
              </Router>
            </LoadingContent>
          </SocketContextWrapper>
        </UserContext.Provider>
      </SnackbarProvider>
    </ThemeProvider>
  );
};

export default withRoot(App);
